import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

/**
 * Format a fraction (0.18) as a percent string ("18"); blank for null/NaN.
 * Preserves up to 3 decimal places of percent (e.g. 0.1804 -> "18.04") so seeding
 * the input and saving it back round-trips the API value instead of silently
 * rounding the commission to one decimal.
 */
function fractionToPercent(fraction: number | string | null | undefined) {
  if (fraction == null || !Number.isFinite(Number(fraction))) return ''
  return String(Math.round(Number(fraction) * 100000) / 1000)
}

// Form-only schema for the commission-rate input. This is a UI-input shape
// (a string, since it comes straight off a text/number field), not the wire
// contract (UpdatePropertyEconomicsRequestSchema takes a 0-1 fraction) -
// mid-edit values here are validated as a percentage a human types, not as
// the API payload it eventually becomes.
const commissionFormSchema = z.object({
  percent: z
    .string()
    .refine(v => v.trim() !== '' && Number.isFinite(Number(v)), {
      message: 'Enter a percentage between 0 and 100.',
    })
    .refine(v => Number(v) >= 0 && Number(v) <= 100, {
      message: 'Enter a percentage between 0 and 100.',
    }),
})

type CommissionFormValues = z.infer<typeof commissionFormSchema>

interface CommissionCardProps {
  propertyId: string
  isActive: boolean
}

interface PropertyEconomics {
  override?: { commission_rate?: number | null } | null
  resolved?: { commission_rate?: number | null } | null
  defaults?: { commission_rate?: number | null } | null
}

/**
 * Per-property management commission editor.
 *
 * Commission is the only economics field that drives owner-net (net revenue =
 * Uplisting net_revenue − commission_rate × (accommodation − discount − channel
 * fee)). Set it to 0 for owned properties (e.g. Naples). Clearing the override
 * reverts the property to the global default. This is purely an economics setting;
 * it does NOT switch any owner to net reporting — that is the per-user toggle.
 */
export function CommissionCard({ propertyId, isActive }: CommissionCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isClearing, setIsClearing] = useState(false)

  const { data, isLoading, error, refetch } = useQuery<PropertyEconomics>({
    queryKey: ['property-economics', propertyId],
    queryFn: () => api.getPropertyEconomics(propertyId),
    staleTime: 30000,
    enabled: !!propertyId && !!isActive,
  })

  const overrideRate = data?.override?.commission_rate
  const resolvedRate = data?.resolved?.commission_rate
  const defaultRate = data?.defaults?.commission_rate
  const isOverridden = overrideRate != null

  const form = useForm<CommissionFormValues>({
    resolver: zodResolver(commissionFormSchema),
    defaultValues: { percent: '' },
  })

  // Seed the input from the resolved (effective) rate ONCE per property. Keying on
  // propertyId (not the query data object) avoids re-seeding — and clobbering an
  // admin's unsaved edit — when react-query refetches (remount/reconnect).
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (data && seededFor.current !== propertyId) {
      form.reset({ percent: fractionToPercent(resolvedRate) })
      seededFor.current = propertyId
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, resolvedRate, propertyId])

  const isBusy = form.formState.isSubmitting || isClearing

  const onSubmit = async (values: CommissionFormValues) => {
    try {
      const pct = Number(values.percent)
      const fraction = Math.round((pct / 100) * 10000) / 10000
      // If the entered value equals the global default, clear the override (for
      // both fresh and already-overridden properties) instead of pinning a
      // redundant one — otherwise the property silently stops following future
      // global commission changes.
      let patch: { commission_rate: number | null }
      let usingDefault = false
      if (defaultRate != null && Math.abs(fraction - Number(defaultRate)) < 1e-9) {
        patch = { commission_rate: null }
        usingDefault = true
      } else {
        patch = { commission_rate: fraction }
      }
      await api.updatePropertyEconomics(propertyId, patch)
      await queryClient.invalidateQueries({ queryKey: ['property-economics', propertyId] })
      // Reflect the persisted value in the input. The seed-once effect won't
      // re-seed for the same property, so set it explicitly here — otherwise after
      // clearing an override the field would keep showing the old override value
      // (and re-saving would recreate it).
      form.reset({ percent: fractionToPercent(usingDefault ? defaultRate : patch.commission_rate) })
      toast({
        title: 'Commission saved',
        description: usingDefault
          ? 'This property uses the global default commission.'
          : `Commission set to ${values.percent}%.`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to save commission',
        description: message || 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleUseDefault = async () => {
    setIsClearing(true)
    try {
      await api.updatePropertyEconomics(propertyId, { commission_rate: null })
      await queryClient.invalidateQueries({ queryKey: ['property-economics', propertyId] })
      form.reset({ percent: fractionToPercent(defaultRate) })
      toast({
        title: 'Commission saved',
        description: 'This property uses the global default commission.',
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to save commission',
        description: message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Commission</CardTitle>
        <CardDescription>
          Management commission used to compute owner net revenue. Set to 0% for owned properties.
          Does not change which owners see net vs gross reporting.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton className="h-20 w-full" />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <FormField
                  control={form.control}
                  name="percent"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-medium text-textMuted">
                        Commission rate (%)
                      </FormLabel>
                      <FormControl>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          disabled={isBusy}
                          className="flex h-8 w-28 rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" size="sm" disabled={isBusy}>
                  Save
                </Button>
                {isOverridden && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isBusy}
                    className="text-xs text-textMuted"
                    onClick={handleUseDefault}
                  >
                    Use global default
                  </Button>
                )}
              </div>
              <p className="text-xs text-textMuted">
                Effective: {fractionToPercent(resolvedRate) || '—'}%
                {isOverridden
                  ? ' (per-property override)'
                  : ` (global default ${fractionToPercent(defaultRate) || '—'}%)`}
              </p>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  )
}
